/**
 * Passkey sign-in in the browser. The server (@simplewebauthn/server) sends request
 * options as JSON with base64url strings and expects the assertion back in the same
 * JSON shape. This is the small conversion both ways, written here rather than pulling
 * in a client library.
 */
import type { PublicKeyRequestOptionsJson } from '@aithora/shared';

export function base64urlToBuffer(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufferToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials?.get;
}

/** Ask the browser for a passkey assertion and return it as the server's JSON shape. */
export async function getPasskeyAssertion(opts: PublicKeyRequestOptionsJson): Promise<unknown> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBuffer(opts.challenge),
    timeout: opts.timeout,
    rpId: opts.rpId,
    userVerification: opts.userVerification,
    allowCredentials: opts.allowCredentials?.map((c) => ({
      id: base64urlToBuffer(c.id),
      type: 'public-key' as const,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was chosen.');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(r.clientDataJSON),
      authenticatorData: bufferToBase64url(r.authenticatorData),
      signature: bufferToBase64url(r.signature),
      userHandle: r.userHandle ? bufferToBase64url(r.userHandle) : undefined,
    },
  };
}
